import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { scrollToBooking } from "../utils/scrollToSection";
import { Helmet } from "react-helmet";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Rating,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  useMediaQuery
} from "@mui/material";
import {
  LocationOn as LocationIcon,
  CheckCircle as CheckIcon,
  Event as EventIcon,
  MusicNote as MusicIcon,
  Star as StarIcon,
  ArrowForward as ArrowIcon,
  ExpandMore as ExpandMoreIcon
} from "@mui/icons-material";
import { getCityBySlug, getAllCities } from "../data/cityData";
import Navbar from "../components/navbar";
import Footer from "../components/footer";
import BookingForm from "../components/bookingForm";

const CityPage = () => {
  const params = useParams();
  const citySlug = params.citySlug || params.slug;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const city = getCityBySlug(citySlug);
  const allCities = getAllCities();
  const nearbyCities = city?.nearby?.map(name =>
    allCities.find(c => c.name === name)
  ).filter(Boolean) || [];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [citySlug]);

  if (!city) {
    return (
      <>
        <Navbar />
        <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
          <Typography variant="h4">City not found</Typography>
          <Button component={Link} to="/" sx={{ mt: 2 }}>Return Home</Button>
        </Container>
        <Footer />
      </>
    );
  }


  return (
    <>
      <Helmet>
        <title>{city.metaTitle}</title>
        <meta name="description" content={city.metaDescription} />
        <meta name="keywords" content={`dhol players ${city.name}, dhol drummers ${city.name}, dhol players near me, ${city.name} wedding dhol, Asian wedding entertainment ${city.name}, Indian wedding drummers ${city.name}, Punjabi dhol ${city.name}, dhol hire ${city.name}, ${city.region} dhol players, dhol drummers near me`} />
        <link rel="canonical" href={`https://imperialdrummers.com/dhol-players/${city.slug}`} />

        <meta property="og:title" content={city.metaTitle} />
        <meta property="og:description" content={city.metaDescription} />
        <meta property="og:url" content={`https://imperialdrummers.com/dhol-players/${city.slug}`} />
        <meta property="og:type" content="website" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": `Imperial Drummers - ${city.name}`,
            "description": city.metaDescription,
            "url": `https://imperialdrummers.com/dhol-players/${city.slug}`,
            "image": "https://imperialdrummers.com/imperial-hero-social.jpg",
            "priceRange": "££-£££",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": city.name,
              "addressRegion": city.region,
              "addressCountry": "GB",
              ...(city.postalCode && { "postalCode": city.postalCode })
            },
            ...(city.geo && {
              "geo": {
                "@type": "GeoCoordinates",
                "latitude": city.geo.lat,
                "longitude": city.geo.lng
              }
            }),
            "areaServed": {
              "@type": "City",
              "name": city.name,
              "containedInPlace": {
                "@type": "AdministrativeArea",
                "name": city.region
              }
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "5",
              "bestRating": "5",
              "worstRating": "1",
              "reviewCount": "2000"
            }
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Dhol Entertainment",
            "name": `Dhol Players in ${city.name}`,
            "description": `Professional dhol drummers for Asian weddings, Indian weddings, and events in ${city.name}, ${city.region}.`,
            "provider": {
              "@type": "LocalBusiness",
              "name": "Imperial Drummers",
              "url": "https://imperialdrummers.com/"
            },
            "areaServed": {
              "@type": "City",
              "name": city.name
            },
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": `Dhol Services in ${city.name}`,
              "itemListElement": [
                {
                  "@type": "Offer",
                  "itemOffered": { "@type": "Service", "name": "Wedding Dhol Entertainment", "description": `Professional dhol players for baraat, milni & wedding celebrations in ${city.name}` }
                },
                {
                  "@type": "Offer",
                  "itemOffered": { "@type": "Service", "name": "Corporate Dhol Entertainment", "description": `Dhol drummers for Diwali, product launches & corporate events in ${city.region}` }
                },
                {
                  "@type": "Offer",
                  "itemOffered": { "@type": "Service", "name": "Cultural Event Dhol", "description": `Dhol entertainment for engagements, birthdays & cultural celebrations in ${city.name}` }
                }
              ]
            }
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://imperialdrummers.com/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Dhol Players",
                "item": "https://imperialdrummers.com/"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": `Dhol Players ${city.name}`,
                "item": `https://imperialdrummers.com/dhol-players/${city.slug}`
              }
            ]
          })}
        </script>

        {city.reviews.map((review, i) => (
          <script type="application/ld+json" key={`review-${i}`}>
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Review",
              "reviewRating": {
                "@type": "Rating",
                "ratingValue": "5",
                "bestRating": "5",
                "worstRating": "1"
              },
              "author": {
                "@type": "Person",
                "name": review.author
              },
              "reviewBody": review.quote,
              "itemReviewed": {
                "@type": "LocalBusiness",
                "name": `Imperial Drummers - ${city.name}`,
                "url": `https://imperialdrummers.com/dhol-players/${city.slug}`
              }
            })}
          </script>
        ))}

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": city.faqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
              }
            }))
          })}
        </script>
      </Helmet>

      <Navbar />

      <Box component="main">
        {/* Hero Section */}
        <Box sx={{
          background: 'linear-gradient(135deg, #5C005D 0%, #1a0a2e 100%)',
          color: 'white',
          pt: { xs: 14, md: 18 },
          pb: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Container maxWidth="lg">
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={7}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <LocationIcon sx={{ mr: 1, color: '#ffd700' }} />
                  <Typography variant="subtitle1" sx={{ color: '#ffd700' }}>
                    {city.region}, United Kingdom
                  </Typography>
                </Box>

                <Typography
                  variant={isMobile ? "h3" : "h1"}
                  component="h1"
                  sx={{ fontWeight: 'bold', mb: 3 }}
                >
                  {city.h1}
                </Typography>

                <Typography variant="h6" sx={{ mb: 4, opacity: 0.9, lineHeight: 1.7 }}>
                  {city.intro}
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={scrollToBooking}
                    startIcon={<EventIcon />}
                    sx={{
                      px: 4,
                      py: 1.5,
                      backgroundColor: '#ffd700',
                      color: '#1a0a2e',
                      fontWeight: 'bold',
                      '&:hover': { backgroundColor: '#e6c200' }
                    }}
                  >
                    Get Free Quote
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    component={Link}
                    to="/"
                    sx={{
                      px: 4,
                      py: 1.5,
                      borderColor: 'white',
                      color: 'white',
                      '&:hover': { borderColor: '#ffd700', color: '#ffd700' }
                    }}
                  >
                    View Our Work
                  </Button>
                </Box>
              </Grid>

              <Grid item xs={12} md={5}>
                <Paper sx={{
                  p: 3,
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 2,
                  border: '1px solid rgba(255,255,255,0.2)'
                }}>
                  <Typography variant="h6" sx={{ color: '#ffd700', mb: 2, fontWeight: 'bold' }}>
                    Why Choose Us in {city.name}?
                  </Typography>
                  <List dense>
                    {[
                      "Professional dhol players with 15+ years experience",
                      "Authentic Punjabi & Bollywood performances",
                      "Full coverage across " + city.region,
                      "Luxury outfit options available",
                      "Flexible packages for all budgets"
                    ].map((item, i) => (
                      <ListItem key={i} sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckIcon sx={{ color: '#ffd700' }} />
                        </ListItemIcon>
                        <ListItemText primary={item} sx={{ color: 'white' }} />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Services Section */}
        <Box sx={{ py: { xs: 5, md: 8 }, backgroundColor: '#f8f5fb' }}>
          <Container maxWidth="lg">
            <Typography variant="h2" align="center" sx={{ color: '#5C005D', mb: 2, fontSize: { xs: '1.75rem', md: '3rem' } }}>
              Dhol Entertainment Services in {city.name}
            </Typography>
            <Typography variant="h6" align="center" sx={{ color: '#666', mb: 6, maxWidth: 700, mx: 'auto' }}>
              Professional dhol players available for all types of celebrations across {city.region}
            </Typography>

            <Grid container spacing={4}>
              {[
                {
                  title: "Asian Wedding Dhol",
                  description: `Professional dhol drummers for baraat entrance, milni ceremony, and wedding celebrations in ${city.name}. Perfect for Punjabi, Sikh, and Hindu weddings.`,
                  icon: <MusicIcon sx={{ fontSize: 40 }} />
                },
                {
                  title: "Corporate Events",
                  description: `Dhol entertainment for Diwali celebrations, product launches, and corporate events across ${city.region}. Make your event unforgettable.`,
                  icon: <EventIcon sx={{ fontSize: 40 }} />
                },
                {
                  title: "Cultural Celebrations",
                  description: `From engagement parties to birthday celebrations, our ${city.name} dhol players bring authentic energy to every occasion.`,
                  icon: <StarIcon sx={{ fontSize: 40 }} />
                }
              ].map((service, i) => (
                <Grid item xs={12} md={4} key={i}>
                  <Card sx={{ height: '100%', '&:hover': { transform: 'translateY(-4px)', transition: '0.3s' } }}>
                    <CardContent sx={{ textAlign: 'center', p: 4 }}>
                      <Box sx={{ color: '#5C005D', mb: 2 }}>{service.icon}</Box>
                      <Typography variant="h5" sx={{ color: '#5C005D', mb: 2 }}>{service.title}</Typography>
                      <Typography variant="body1" color="text.secondary">{service.description}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Areas Covered */}
        <Box sx={{ py: { xs: 5, md: 8 }, backgroundColor: 'white' }}>
          <Container maxWidth="lg">
            <Typography variant="h2" align="center" sx={{ color: '#5C005D', mb: 2, fontSize: { xs: '1.75rem', md: '3rem' } }}>
              Areas We Cover in {city.region}
            </Typography>
            <Typography variant="h6" align="center" sx={{ color: '#666', mb: 4 }}>
              Dhol players available throughout {city.name} and surrounding areas
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1.5 }}>
              {city.areas.map((area, i) => (
                <Chip
                  key={i}
                  label={area}
                  sx={{
                    backgroundColor: '#5C005D',
                    color: 'white',
                    fontSize: '1rem',
                    py: 2.5,
                    px: 1,
                    '&:hover': { backgroundColor: '#7a007b' }
                  }}
                />
              ))}
            </Box>
          </Container>
        </Box>

        {/* Popular Venues */}
        <Box sx={{ py: { xs: 5, md: 8 }, backgroundColor: '#f8f5fb' }}>
          <Container maxWidth="lg">
            <Typography variant="h2" align="center" sx={{ color: '#5C005D', mb: 2, fontSize: { xs: '1.75rem', md: '3rem' } }}>
              Popular {city.name} Wedding Venues
            </Typography>
            <Typography variant="h6" align="center" sx={{ color: '#666', mb: 4 }}>
              We've performed at the finest venues across {city.region}
            </Typography>

            <Grid container spacing={2} justifyContent="center">
              {city.venues.map((venue, i) => (
                <Grid item xs={12} sm={6} md={3} key={i}>
                  <Paper sx={{ p: 2, textAlign: 'center', height: '100%' }}>
                    <LocationIcon sx={{ color: '#5C005D', mb: 1 }} />
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{venue}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Reviews from couples and clients in this city */}
        {city.reviews.length > 0 && (
          <Box sx={{ py: { xs: 5, md: 8 }, backgroundColor: '#1a0a2e', color: 'white' }}>
            <Container maxWidth="md">
              {city.reviews.length > 1 && (
                <Typography variant="h2" align="center" sx={{ mb: 5, fontSize: { xs: '1.75rem', md: '3rem' } }}>
                  What {city.name} Couples Say
                </Typography>
              )}
              {city.reviews.map((review, i) => (
                <Box key={i} sx={{ textAlign: 'center', mb: i < city.reviews.length - 1 ? 6 : 0 }}>
                  <Rating value={5} readOnly sx={{ mb: 3, '& .MuiRating-iconFilled': { color: '#ffd700' } }} size="large" />
                  <Typography variant="h5" sx={{ fontStyle: 'italic', mb: 3, lineHeight: 1.6 }}>
                    "{review.quote}"
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Avatar sx={{ bgcolor: '#5C005D', mr: 2 }}>
                      {review.author.charAt(0)}
                    </Avatar>
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {review.author}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        {review.event}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Container>
          </Box>
        )}

        {/* Events we've played in this city */}
        {city.venuesPlayed?.length > 0 && (
          <Box sx={{ py: { xs: 5, md: 8 }, backgroundColor: 'white' }}>
            <Container maxWidth="lg">
              <Typography variant="h2" align="center" sx={{ color: '#5C005D', mb: 4, fontSize: { xs: '1.75rem', md: '3rem' } }}>
                Recent {city.name} Performances
              </Typography>
              <Grid container spacing={2} justifyContent="center">
                {city.venuesPlayed.map((gig, i) => (
                  <Grid item xs={12} sm={6} md={4} key={i}>
                    <Paper sx={{ p: 2.5, height: '100%' }}>
                      <Typography variant="h6" sx={{ color: '#5C005D' }}>{gig.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {[gig.event, gig.year].filter(Boolean).join(' · ')}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Container>
          </Box>
        )}

        {/* Local photos and videos */}
        {city.gallery?.length > 0 && (
          <Box sx={{ py: { xs: 5, md: 8 }, backgroundColor: '#f8f5fb' }}>
            <Container maxWidth="lg">
              <Typography variant="h2" align="center" sx={{ color: '#5C005D', mb: 4, fontSize: { xs: '1.75rem', md: '3rem' } }}>
                Dhol in {city.name}: Photos &amp; Videos
              </Typography>
              <Grid container spacing={2}>
                {city.gallery.map((item, i) => (
                  <Grid item xs={12} sm={6} md={4} key={i}>
                    <Box sx={{ position: 'relative', paddingTop: '75%', borderRadius: 2, overflow: 'hidden', backgroundColor: '#1a0a2e' }}>
                      {item.type === 'video' ? (
                        <Box
                          component="iframe"
                          src={item.src}
                          title={item.title || `Imperial Drummers in ${city.name}`}
                          loading="lazy"
                          allow="encrypted-media; picture-in-picture"
                          allowFullScreen
                          sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
                        />
                      ) : (
                        <Box
                          component="img"
                          src={item.src}
                          alt={item.alt || `Imperial Drummers dhol players in ${city.name}`}
                          loading="lazy"
                          sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      )}
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Container>
          </Box>
        )}

        {/* Prices and travel / coverage */}
        {(city.pricing || city.coverageNotes) && (
          <Box sx={{ py: { xs: 5, md: 8 }, backgroundColor: 'white' }}>
            <Container maxWidth="md">
              <Typography variant="h2" align="center" sx={{ color: '#5C005D', mb: 4, fontSize: { xs: '1.75rem', md: '3rem' } }}>
                Prices &amp; Travel in {city.name}
              </Typography>
              {city.pricing && (
                <Typography variant="h5" align="center" sx={{ mb: 2 }}>
                  From <Box component="strong" sx={{ color: '#5C005D' }}>{city.pricing.from}</Box>
                  {city.pricing.note && (
                    <Typography component="span" variant="body1" color="text.secondary"> ({city.pricing.note})</Typography>
                  )}
                </Typography>
              )}
              {city.coverageNotes && (
                <Typography variant="body1" align="center" sx={{ lineHeight: 1.8, fontSize: '1.1rem' }}>
                  {city.coverageNotes}
                </Typography>
              )}
            </Container>
          </Box>
        )}

        {/* Local Content Section */}
        <Box sx={{ py: { xs: 5, md: 8 }, backgroundColor: 'white' }}>
          <Container maxWidth="lg">
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography variant="h2" sx={{ color: '#5C005D', mb: 3, fontSize: { xs: '1.75rem', md: '3rem' } }}>
                  Your Local {city.name} Dhol Experts
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: '1.1rem' }}>
                  {city.localContent}
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: '1.1rem' }}>
                  With over 2000 events performed across the UK, Imperial Drummers brings unmatched experience
                  and professionalism to every {city.name} celebration. Our drummers are not just musicians
                  - they're entertainers who understand how to read the crowd and create unforgettable moments.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={scrollToBooking}
                  sx={{
                    backgroundColor: '#5C005D',
                    '&:hover': { backgroundColor: '#7a007b' }
                  }}
                >
                  Book {city.name} Dhol Players
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 4, backgroundColor: '#f8f5fb' }}>
                  <Typography variant="h5" sx={{ color: '#5C005D', mb: 3 }}>
                    What's Included
                  </Typography>
                  <List>
                    {[
                      "Professional dhol players (solo to full ensemble)",
                      "Authentic traditional costumes",
                      "Premium dhol instruments",
                      "Sound equipment if required",
                      "Coordination with venue & planners",
                      "Flexible performance duration"
                    ].map((item, i) => (
                      <ListItem key={i}>
                        <ListItemIcon>
                          <CheckIcon sx={{ color: '#5C005D' }} />
                        </ListItemIcon>
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* FAQ Section */}
        <Box sx={{ py: { xs: 5, md: 8 }, backgroundColor: '#f8f5fb' }}>
          <Container maxWidth="lg">
            <Typography variant="h2" align="center" sx={{ color: '#5C005D', mb: 2, fontSize: { xs: '1.75rem', md: '3rem' } }}>
              Frequently Asked Questions
            </Typography>
            <Typography variant="h6" align="center" sx={{ color: '#666', mb: 4 }}>
              Common questions about hiring dhol players in {city.name}
            </Typography>
            <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
              {city.faqs.map((faq, index) => (
                <Accordion key={index} sx={{ mb: 1, '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6" sx={{ color: '#5C005D', fontSize: { xs: '1rem', md: '1.25rem' } }}>
                      {faq.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body1" sx={{ color: '#666' }}>
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </Container>
        </Box>

        {/* Nearby Cities */}
        {nearbyCities.length > 0 && (
          <Box sx={{ py: { xs: 4, md: 6 }, backgroundColor: '#f8f5fb' }}>
            <Container maxWidth="lg">
              <Typography variant="h4" align="center" sx={{ color: '#5C005D', mb: 4 }}>
                Also Serving Nearby Areas
              </Typography>
              <Grid container spacing={3} justifyContent="center">
                {nearbyCities.map((nearbyCity) => (
                  <Grid item xs={12} sm={4} key={nearbyCity.slug}>
                    <Card
                      component={Link}
                      to={`/dhol-players/${nearbyCity.slug}`}
                      sx={{
                        textDecoration: 'none',
                        display: 'block',
                        '&:hover': { transform: 'translateY(-4px)', transition: '0.3s' }
                      }}
                    >
                      <CardContent sx={{ textAlign: 'center' }}>
                        <LocationIcon sx={{ color: '#5C005D', fontSize: 32, mb: 1 }} />
                        <Typography variant="h6" sx={{ color: '#5C005D' }}>
                          Dhol Players {nearbyCity.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {nearbyCity.region}
                        </Typography>
                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#5C005D' }}>
                          <Typography variant="body2">View</Typography>
                          <ArrowIcon sx={{ fontSize: 16, ml: 0.5 }} />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Container>
          </Box>
        )}

        {/* Booking Form */}
        <Box sx={{ py: { xs: 5, md: 8 }, backgroundColor: 'white' }}>
          <BookingForm />
        </Box>

        <Footer />
      </Box>
    </>
  );
};

export default CityPage;
